import {
  NodeToolbar,
  Position,
  useStore,
  type ReactFlowState,
} from '@xyflow/react';
import { SelectionAllIcon } from '@phosphor-icons/react';
import { T } from 'gt-react';
import { getStepLayoutSelectionRoots } from 'src/lib/workflows/stepLayoutGroups';

interface SelectionQuickActionsProps {
  /** Hidden when false — read-only graphs and ungroupable selections. */
  enabled: boolean;
  onGroupNodes: () => void;
}

/**
 * Floating "Group Nodes" button that appears above a multi-node selection.
 *
 * Anchored to the selection rather than parked in a corner `<Panel>`, so on a
 * large canvas the action shows up where the user is looking.
 *
 * Positioning is delegated to React Flow's `NodeToolbar`, which takes a list of
 * node ids and keeps itself glued to their bounds. Doing it by hand meant
 * subscribing to the viewport transform and calling `getBoundingClientRect` on
 * every pan, zoom, and drag frame — a forced layout per frame, which was a
 * visible stutter while dragging.
 */
export function SelectionQuickActions({
  enabled,
  onGroupNodes,
}: SelectionQuickActionsProps) {
  // Selecting on the joined id string rather than the node array keeps this from
  // re-rendering on every unrelated node change.
  // Entities, not nodes: a selected box counts once and the steps inside it
  // aren't counted separately, so the badge matches what grouping will wrap.
  const selectedIds = useStore((state: ReactFlowState) =>
    getStepLayoutSelectionRoots(state.nodes)
      .map((node) => node.id)
      .join(','),
  );

  const ids = selectedIds.length > 0 ? selectedIds.split(',') : [];
  const isVisible = enabled && ids.length >= 2;

  // `NodeToolbar` needs a non-empty id list even while hidden, or it warns about
  // an unknown node.
  if (ids.length === 0) return null;

  return (
    <NodeToolbar
      nodeId={ids}
      isVisible={isVisible}
      position={Position.Top}
      offset={12}
      // `nodrag`/`nopan` keep a click on the button from starting a canvas
      // gesture underneath it.
      className="nodrag nopan"
    >
      <button
        type="button"
        onClick={onGroupNodes}
        className="flex items-center gap-1.5 rounded-full border border-border bg-popover px-3 py-1.5 text-xs font-medium text-popover-foreground shadow-md hover:bg-accent hover:text-accent-foreground"
      >
        <SelectionAllIcon className="h-3.5 w-3.5" />
        <T context="quick action button shown above a multi-node selection in the workflow editor">
          Group Nodes
        </T>
        <span className="text-muted-foreground">{ids.length}</span>
      </button>
    </NodeToolbar>
  );
}
